import { AxiosHeaders, AxiosRequestHeaders } from 'axios';
import api from './api';

export const DEFAULT_PRIMARY_COLOR = '#3B82F6';
export const DEFAULT_SECONDARY_COLOR = '#64748B';

export interface OrganizationBranding {
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string | null;
}

export interface BrandingUpdate {
  primaryColor: string;
  secondaryColor: string;
  logo?: File | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function pickString(source: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    const nested = asRecord(value);
    if (nested) {
      const url = pickString(nested, ['url', 'logo_url', 'logoUrl', 'href']);
      if (url) return url;
    }
  }
  return null;
}

function normalizeHex(value: string | null, fallback: string): string {
  if (!value) return fallback;
  const withHash = value.startsWith('#') ? value : `#${value}`;
  if (/^#[0-9A-Fa-f]{6}$/.test(withHash)) return withHash.toUpperCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(withHash)) {
    const hex = withHash.slice(1);
    return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`.toUpperCase();
  }
  return fallback;
}

export function mapBranding(body: unknown): OrganizationBranding {
  const root = asRecord(body) ?? {};
  const data = asRecord(root.data) ?? root;
  const source = asRecord(data.organization) ?? asRecord(data.branding) ?? data;
  const logo = pickString(source, ['logo_url', 'logoUrl', 'logo', 'image_url', 'imageUrl']);
  return {
    primaryColor: normalizeHex(pickString(source, ['primary_color', 'primaryColor']), DEFAULT_PRIMARY_COLOR),
    secondaryColor: normalizeHex(pickString(source, ['secondary_color', 'secondaryColor']), DEFAULT_SECONDARY_COLOR),
    logoUrl: logo && !logo.startsWith('#') ? logo : null,
  };
}

export const brandingService = {
  get: async (): Promise<OrganizationBranding> => {
    const response = await api.get('/api/auth/organizations/branding');
    return mapBranding(response.data);
  },

  update: async (payload: BrandingUpdate): Promise<OrganizationBranding> => {
    const form = new FormData();
    form.append('primary_color', payload.primaryColor);
    form.append('secondary_color', payload.secondaryColor);
    if (payload.logo) form.append('logo', payload.logo);
    const response = await api.patch('/api/auth/organizations/branding', form, {
      transformRequest: [
        (data, headers: AxiosRequestHeaders) => {
          const bag = headers as AxiosHeaders & Record<string, unknown>;
          if (typeof bag.delete === 'function') {
            bag.delete('Content-Type');
            bag.delete('content-type');
          } else {
            delete bag['Content-Type'];
            delete bag['content-type'];
          }
          return data;
        },
      ],
    });
    return mapBranding(response.data);
  },
};
